/**
 * Comments on the nodes of a canvas: a conversation about one node, for the people a workspace is
 * shared with (feedback on a picture, a question about a prompt). Each comment says which node it
 * is about, who wrote it and when. Kept in the cloud under the workspace (utils/commentStore.ts).
 */

export const MAX_COMMENT_CHARS = 4000;

export interface NodeComment {
    id: string;
    /** The node it is about */
    nodeId: string;
    text: string;
    authorId: string;
    authorName: string;
    authorPicture?: string;
    createdAt: number;
}

/** A comment as read back from the cloud, or null when it is not one */
export const toComment = (id: string, data: Record<string, unknown>): NodeComment | null => {
    if (typeof data.nodeId !== 'string' || typeof data.text !== 'string' || typeof data.authorId !== 'string') return null;
    return {
        id,
        nodeId: data.nodeId,
        text: data.text,
        authorId: data.authorId,
        authorName: typeof data.authorName === 'string' && data.authorName ? data.authorName : 'Someone',
        ...(typeof data.authorPicture === 'string' && data.authorPicture ? { authorPicture: data.authorPicture } : {}),
        createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
    };
};

/** What is written: the text as typed, trimmed at its ends; null when there is nothing to send */
export const cleanComment = (text: string): string | null => {
    const trimmed = text.replace(/^\s+|\s+$/g, '');
    if (!trimmed) return null;
    return trimmed.slice(0, MAX_COMMENT_CHARS);
};

/** One node's comments, oldest first (a conversation reads down) */
export const commentsOf = (comments: ReadonlyArray<NodeComment>, nodeId: string): NodeComment[] =>
    comments.filter(comment => comment.nodeId === nodeId).sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));

/** How many comments each node has */
export const commentCounts = (comments: ReadonlyArray<NodeComment>): Record<string, number> => {
    const counts: Record<string, number> = {};
    for (const comment of comments) counts[comment.nodeId] = (counts[comment.nodeId] ?? 0) + 1;
    return counts;
};

/** Who may delete a comment: whoever wrote it, and the workspace's owner */
export const mayDeleteComment = (comment: Pick<NodeComment, 'authorId'>, userId: string | undefined, isOwner: boolean): boolean =>
    !!userId && (comment.authorId === userId || isOwner);
